import { createEffect, createSignal, For, onCleanup, Show } from "solid-js";
import { useLocale } from "../i18n/LocaleProvider";

interface Target {
  key: string;
  label: string;
  href: (text: string, url: string) => string;
}

const TARGETS: readonly Target[] = [
  {
    key: "x",
    label: "X / Twitter",
    href: (t, u) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(t)}&url=${encodeURIComponent(u)}`,
  },
  {
    key: "telegram",
    label: "Telegram",
    href: (t, u) =>
      `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}`,
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    href: (t, u) => `https://wa.me/?text=${encodeURIComponent(`${t} ${u}`)}`,
  },
  {
    key: "facebook",
    label: "Facebook",
    href: (_t, u) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`,
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    href: (_t, u) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}`,
  },
  {
    key: "reddit",
    label: "Reddit",
    href: (t, u) =>
      `https://www.reddit.com/submit?url=${encodeURIComponent(u)}&title=${encodeURIComponent(t)}`,
  },
  {
    key: "email",
    label: "Email",
    href: (t, u) =>
      `mailto:?subject=${encodeURIComponent("Ikamiro")}&body=${encodeURIComponent(`${t}\n\n${u}`)}`,
  },
];

interface Props {
  url: string;
  text: string;
  title?: string;
  variant: "fab" | "inline";
  label?: string;
  ariaLabel?: string;
  testId?: string;
}

const ShareIcon = () => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    class="w-5 h-5"
    fill="none"
    stroke="currentColor"
    stroke-width="1.8"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
    <line x1="15.4" y1="6.5" x2="8.6" y2="10.5" />
  </svg>
);

export default function ShareButton(props: Props) {
  const [open, setOpen] = createSignal(false);
  const [copied, setCopied] = createSignal(false);
  const { t } = useLocale();

  const canNativeShare = (): boolean =>
    typeof navigator !== "undefined" && typeof (navigator as Navigator).share === "function";

  async function onClick(e: MouseEvent) {
    e.stopPropagation();
    if (canNativeShare()) {
      try {
        await (navigator as Navigator).share({
          title: props.title,
          text: props.text,
          url: props.url,
        });
        return;
      } catch {
        // User cancelled or share failed — fall through to popover.
      }
    }
    setOpen(true);
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(props.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard might be blocked — leave UI as-is.
    }
  }

  createEffect(() => {
    if (!open()) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });

  const buttonClass = (): string =>
    props.variant === "fab"
      ? "inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-900/80 text-white border border-slate-700 hover:bg-slate-800 transition shadow-lg shadow-black/30"
      : "inline-flex items-center gap-1.5 rounded-full bg-white/10 text-white text-sm px-3 py-1.5 hover:bg-white/20 transition";

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        class={buttonClass()}
        aria-label={props.ariaLabel ?? t("shareAria")}
        data-testid={props.testId}
      >
        <ShareIcon />
        <Show when={props.variant === "inline" && props.label}>
          <span>{props.label}</span>
        </Show>
      </button>

      <Show when={open()}>
        <div
          class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="share-sheet"
        >
          <div
            class="bg-slate-900 border border-slate-700 rounded-2xl p-5 w-[min(420px,90vw)] flex flex-col gap-3 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div class="flex items-center justify-between">
              <h3 class="text-base font-display text-amber-300">{t("shareTitle")}</h3>
              <button
                type="button"
                aria-label={t("close")}
                onClick={() => setOpen(false)}
                class="text-slate-400 hover:text-white text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <p class="text-xs text-slate-400 -mt-1">{props.text}</p>

            <div class="grid grid-cols-2 gap-2 mt-1">
              <For each={TARGETS}>
                {(target) => (
                  <a
                    href={target.href(props.text, props.url)}
                    target="_blank"
                    rel="noreferrer noopener"
                    onClick={() => setOpen(false)}
                    class="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm text-slate-100 text-center transition"
                    data-share-target={target.key}
                  >
                    {target.label}
                  </a>
                )}
              </For>
            </div>

            <button
              type="button"
              onClick={copyLink}
              class="mt-1 px-3 py-2 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 text-sm transition"
            >
              {copied() ? t("shareCopied") : t("shareCopyLink")}
            </button>

            <p class="text-[10px] text-slate-500 mt-1">{t("shareInstagramHint")}</p>
          </div>
        </div>
      </Show>
    </>
  );
}
